import React, { memo, useContext, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { CellValue, IdType } from 'react-table'
import { mdiAlertCircleOutline } from '@mdi/js'
import FileSaver from 'file-saver'

import { templatesPageMessages } from '../../messages'
import theme from 'theme_'
import { Body12, BodyPrimary, Regular } from 'components/Typography/Labels'
import { TEMPLATES_HEADERS } from './../../consts'
import type { Template } from 'features/templates/types'
import NameCell from '../TemplatesTable/NameCell'
import {
  MobileTableBlock,
  MobileTableBlockCell,
  MobileTableBlockHeader,
  MobileTableBlockRow,
} from 'components/Table/MobileBlock/styles'
import { KebabMenu, KebabMenuItem } from 'components/KebabMenu'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { commonMessages } from 'app/i18n/messages'
import { fetchFile } from 'app/utils/fetchFile'
import { SpinnerCentered } from 'components/Loader'
import { ErrorIcon } from 'components/Table/Cell/DownloadButtonCell/styles'
import { TemplatesMobileBlockKebabItemWrapper } from './styles'
import ConfirmDeleteModal from 'components/Table/Cell/DeleteButtonCell/ConfirmDeleteModal'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { deleteTemplateThunk } from 'features/templates/slice'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'

interface TemplatesMobileBlockProps {
  values: Record<IdType<Template>, CellValue>
}

export function TemplatesMobileBlock({ values }: TemplatesMobileBlockProps) {
  const { formatMessage } = useIntl()
  const { hasRootAdminPermission } = useContext(CurrentUserPermissionsContext)

  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false)

  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const dispatch = useAppDispatch()

  const handleDeleteClick = async () => {
    if (rootDomainId) {
      await dispatch(
        deleteTemplateThunk(rootDomainId, values[TEMPLATES_HEADERS.ID])
      )
      setIsConfirmDeleteModalOpen(false)
    }
  }

  const handleDownloadClick = async () => {
    setIsLoading(true)
    try {
      const name = values[TEMPLATES_HEADERS.NAME]
      const url = values[TEMPLATES_HEADERS.DOWNLOAD_LINK]
      const { data } = await fetchFile({
        url,
        responseType: 'blob',
        contentType: 'application/octet-stream',
      })

      FileSaver.saveAs(data, `${name}.tar.gz`)
    } catch (error) {
      setShowError(true)
      console.log('Error downloading file', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <MobileTableBlock>
        <MobileTableBlockRow>
          <MobileTableBlockHeader>
            <BodyPrimary bold color={theme.colors.otterWarmBlack}>
              <NameCell name={values[TEMPLATES_HEADERS.NAME]} />
            </BodyPrimary>
          </MobileTableBlockHeader>
          <KebabMenu>
            <KebabMenuItem
              title={
                <TemplatesMobileBlockKebabItemWrapper>
                  <FormattedMessage {...commonMessages.download} />
                  {isLoading && <SpinnerCentered size="16" />}
                  {showError && (
                    <ErrorIcon
                      path={mdiAlertCircleOutline}
                      title={formatMessage(commonMessages.commonError)}
                      description={formatMessage(commonMessages.commonError)}
                      size={0.8}
                    />
                  )}
                </TemplatesMobileBlockKebabItemWrapper>
              }
              onClick={handleDownloadClick}
            />
            {hasRootAdminPermission && (
              <KebabMenuItem
                title={<FormattedMessage {...commonMessages.delete} />}
                onClick={() => setIsConfirmDeleteModalOpen(true)}
              />
            )}
          </KebabMenu>
        </MobileTableBlockRow>

        <MobileTableBlockRow>
          <MobileTableBlockCell>
            <Body12 color={theme.colors.darkeyGrey}>
              {formatMessage(templatesPageMessages.uploadedColumn)}
            </Body12>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <BodyPrimary color={theme.colors.otterWarmBlack}>
              <IntlFormattedDate
                date={values[TEMPLATES_HEADERS.UPLOADED]}
                withTime
              />
            </BodyPrimary>
          </MobileTableBlockCell>
        </MobileTableBlockRow>

        <MobileTableBlockRow>
          <MobileTableBlockCell>
            <Body12 color={theme.colors.darkeyGrey}>
              {formatMessage(templatesPageMessages.uploadedByColumn)}
            </Body12>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <BodyPrimary color={theme.colors.otterWarmBlack}>
              {values[TEMPLATES_HEADERS.INTEGRATOR_NAME]}
            </BodyPrimary>
          </MobileTableBlockCell>
        </MobileTableBlockRow>

        <MobileTableBlockRow>
          <MobileTableBlockCell>
            <Body12 color={theme.colors.darkeyGrey}>
              {formatMessage(templatesPageMessages.versionColumn)}
            </Body12>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <BodyPrimary color={theme.colors.otterWarmBlack}>
              {values[TEMPLATES_HEADERS.VERSION]}
            </BodyPrimary>
          </MobileTableBlockCell>
        </MobileTableBlockRow>
      </MobileTableBlock>
      {isConfirmDeleteModalOpen && (
        <ConfirmDeleteModal
          isOpen
          handleDiscard={() => setIsConfirmDeleteModalOpen(false)}
          handleConfirm={handleDeleteClick}
          title={formatMessage(templatesPageMessages.deleteTemplateModalTitle)}
        >
          <Regular>
            {formatMessage(
              templatesPageMessages.deleteTemplateModalDescription,
              { name: values[TEMPLATES_HEADERS.NAME], br: <br /> }
            )}
          </Regular>
        </ConfirmDeleteModal>
      )}
    </>
  )
}

export default memo(TemplatesMobileBlock)
